import { Loader2, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRiderDeliveryHistory } from "@/hooks/use-rider-deliveries";
import { formatPrice } from "@/lib/format-price";

const TERMINAL_TIMESTAMP_LABELS: Record<string, string> = {
  delivered: "rider.history.deliveredAt",
  failed: "rider.history.failedAt",
  cancelled: "rider.history.cancelledAt",
};

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}

function terminalTimestamp(delivery: {
  deliveredAt: string | null;
  failedAt: string | null;
}): string | null {
  return delivery.deliveredAt ?? delivery.failedAt;
}

export default function RiderHistoryPage() {
  const { t } = useTranslation();
  const query = useRiderDeliveryHistory();

  if (query.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const items = query.data ?? [];

  if (items.length === 0) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {t("rider.history.empty", "No tenés pedidos en el historial.")}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ul className="space-y-2 p-3">
      {items.map((entry) => {
        const ts = terminalTimestamp(entry.delivery);
        const tsLabel =
          TERMINAL_TIMESTAMP_LABELS[entry.delivery.state] ?? "rider.history.completedAt";
        return (
          <li key={entry.delivery.id}>
            <Link to={`/rider/deliveries/${entry.delivery.id}`}>
              <Card className="transition hover:bg-accent/30">
                <CardContent className="p-4">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-base font-semibold">#{entry.order.dailyNumber}</span>
                    <Badge variant="secondary">{entry.delivery.state}</Badge>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {entry.delivery.addressSnapshot?.label ?? ""}
                      {entry.delivery.addressSnapshot?.label ? " — " : ""}
                      {entry.delivery.addressSnapshot?.line1 ?? ""}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="font-medium">
                      {formatPrice(entry.order.total, entry.order.currency)}
                    </span>
                    {ts ? (
                      <span className="text-muted-foreground">
                        {t(tsLabel, "Completado")} {formatTimestamp(ts)}
                      </span>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
